/**
 * Accordion container component.
 * Wraps accordion items and manages expansion state.
 * @module components/ui/Accordion/Accordion
 */

import { useCallback, useMemo, useState } from "react";

import type { ReactNode } from "react";
import { Join } from "react-daisyui";

import { AccordionControls } from "./AccordionControls";
import { AccordionContext, type AccordionContextValue } from "./context";

export { AccordionContext, type AccordionContextValue, useAccordion } from "./context";

export interface AccordionProps {
  /** Unique ID for the accordion */
  id: string;
  /** Accordion items */
  children: ReactNode;
  /** Keys of items that should be expanded by default */
  defaultExpandedKeys?: string[];
  /**
   * At most one item open: opening one closes the rest, and of the defaults
   * only the last opens. Leave out `controls`, whose expand-all
   * contradicts it.
   */
  exclusive?: boolean;
  /**
   * Render one expand-all / collapse-all toggle above the items, once there
   * are three or more. Kept outside the join, whose corner rules would
   * otherwise flatten the button.
   */
  controls?: boolean;
  /**
   * The accordion's own heading, drawn in the same row as the toggle so the
   * two read as one. Needs `controls`.
   */
  heading?: ReactNode;
  /** Additional CSS classes */
  className?: string;
  /** Test ID for the accordion */
  "data-testid"?: string;
  /** Aria labelledby ID */
  "aria-labelledby"?: string;
}

/**
 * Accordion container component.
 */
export function Accordion({
  id,
  children,
  defaultExpandedKeys = [],
  exclusive = false,
  controls = false,
  heading = undefined,
  className = "",
  "data-testid": testId,
  "aria-labelledby": ariaLabelledby,
}: AccordionProps) {
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(
    () => new Set(exclusive ? defaultExpandedKeys.slice(-1) : defaultExpandedKeys),
  );
  const [registeredKeys, setRegisteredKeys] = useState<Set<string>>(new Set());
  const [seenDefaults, setSeenDefaults] = useState(() => new Set(defaultExpandedKeys));

  // A key that joins the defaults after mount opens; one the reader has since
  // closed stays closed. Adjusted while rendering, not in an effect, so the
  // item's fields exist in the commit that added it: a parent focusing one
  // from its own layout effect otherwise finds nothing there.
  const added = defaultExpandedKeys.filter((key) => !seenDefaults.has(key));
  if (added.length > 0) {
    setSeenDefaults(new Set([...seenDefaults, ...added]));
    setExpandedKeys((prev) => new Set(exclusive ? added.slice(-1) : [...prev, ...added]));
  }

  const toggleItem = useCallback(
    (key: string) => {
      setExpandedKeys((prev) => {
        const next = new Set(exclusive ? [] : prev);
        if (prev.has(key)) {
          next.delete(key);
        } else {
          next.add(key);
        }
        return next;
      });
    },
    [exclusive],
  );

  const expandAll = useCallback(() => {
    setExpandedKeys(new Set(registeredKeys));
  }, [registeredKeys]);

  const collapseAll = useCallback(() => {
    setExpandedKeys(new Set());
  }, []);

  const registerItem = useCallback((key: string) => {
    setRegisteredKeys((prev) => {
      if (prev.has(key)) {
        return prev;
      }
      const next = new Set(prev);
      next.add(key);
      return next;
    });
    return () => {
      setRegisteredKeys((prev) => {
        if (!prev.has(key)) {
          return prev;
        }
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    };
  }, []);

  // Memoised because it is a context value: a fresh object every render
  // re-renders every consumer, and the four callbacks below it are already
  // stable, so only expandedKeys should be able to move it.
  const contextValue = useMemo<AccordionContextValue>(
    () => ({
      expandedKeys,
      toggleItem,
      expandAll,
      collapseAll,
      itemCount: registeredKeys.size,
      openCount: [...expandedKeys].filter((key) => registeredKeys.has(key)).length,
      registerItem,
    }),
    [expandedKeys, toggleItem, expandAll, collapseAll, registeredKeys, registerItem],
  );

  return (
    <AccordionContext.Provider value={contextValue}>
      {controls && <AccordionControls accordionId={id} heading={heading} />}
      <Join
        vertical
        id={id}
        className={`w-full bg-base-100${className ? ` ${className}` : ""}`}
        data-testid={testId}
        aria-labelledby={ariaLabelledby}
      >
        {children}
      </Join>
    </AccordionContext.Provider>
  );
}

export default Accordion;
